/**
 * OrderTicket - Professional Trading Order Entry Component
 *
 * Full-featured order ticket with support for market, limit, stop, stop-limit,
 * and trailing stop orders. Includes bracket orders (take profit / stop loss),
 * time-in-force selection, and real-time quote integration.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  X,
  Search,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Clock,
  Shield,
  Target,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  ChevronDown,
  Zap,
  Info,
} from 'lucide-react';
import { brokerAPI } from '../../api/broker';
import type { PlaceOrderRequest, BrokerOrder, BrokerAccount } from '../../api/broker';

// ============================================================================
// Types
// ============================================================================

type OrderSide = 'buy' | 'sell';
type OrderType = 'market' | 'limit' | 'stop' | 'stop_limit' | 'trailing_stop';
type TimeInForce = 'day' | 'gtc' | 'ioc' | 'fok';
type TrailMode = 'percent' | 'price';

interface OrderTicketProps {
  prefillSymbol?: string;
  prefillSide?: OrderSide;
  prefillQty?: number;
  onOrderPlaced?: (order: BrokerOrder) => void;
  onClose?: () => void;
}

interface Toast {
  type: 'success' | 'error';
  title: string;
  message: string;
}

interface QuoteData {
  bid: number;
  ask: number;
  last: number;
  volume: number;
  change: number;
  change_pct: number;
}

// ============================================================================
// Constants
// ============================================================================

const ORDER_TYPES: { value: OrderType; label: string; desc: string }[] = [
  { value: 'market', label: 'Market', desc: 'Execute immediately at best price' },
  { value: 'limit', label: 'Limit', desc: 'Execute at specified price or better' },
  { value: 'stop', label: 'Stop', desc: 'Trigger market order at stop price' },
  { value: 'stop_limit', label: 'Stop-Limit', desc: 'Trigger limit order at stop price' },
  { value: 'trailing_stop', label: 'Trail Stop', desc: 'Dynamic stop that follows price' },
];

const TIME_IN_FORCE_OPTIONS: { value: TimeInForce; label: string; desc: string }[] = [
  { value: 'day', label: 'DAY', desc: 'Valid until end of trading day' },
  { value: 'gtc', label: 'GTC', desc: 'Good until canceled' },
  { value: 'ioc', label: 'IOC', desc: 'Immediate or cancel' },
  { value: 'fok', label: 'FOK', desc: 'Fill or kill (all or nothing)' },
];

const COMMON_SYMBOLS = [
  'AAPL', 'MSFT', 'GOOGL', 'AMZN', 'NVDA', 'META', 'TSLA', 'SPY',
  'QQQ', 'AMD', 'NFLX', 'DIS', 'BA', 'JPM', 'V', 'WMT',
];

const QTY_PRESETS = [
  { label: '25%', fraction: 0.25 },
  { label: '50%', fraction: 0.5 },
  { label: '75%', fraction: 0.75 },
  { label: '100%', fraction: 1.0 },
];

// ============================================================================
// Helpers
// ============================================================================

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

// ============================================================================
// Component
// ============================================================================

export default function OrderTicket({
  prefillSymbol = '',
  prefillSide = 'buy',
  prefillQty,
  onOrderPlaced,
  onClose,
}: OrderTicketProps) {
  // ── Form State ──────────────────────────────────────────────────────────
  const [symbol, setSymbol] = useState(prefillSymbol.toUpperCase());
  const [side, setSide] = useState<OrderSide>(prefillSide);
  const [orderType, setOrderType] = useState<OrderType>('market');
  const [qty, setQty] = useState<string>(prefillQty?.toString() ?? '');
  const [limitPrice, setLimitPrice] = useState<string>('');
  const [stopPrice, setStopPrice] = useState<string>('');
  const [trailMode, setTrailMode] = useState<TrailMode>('percent');
  const [trailValue, setTrailValue] = useState<string>('');
  const [tif, setTif] = useState<TimeInForce>('day');
  const [takeProfitPrice, setTakeProfitPrice] = useState<string>('');
  const [stopLossPrice, setStopLossPrice] = useState<string>('');
  const [showBracket, setShowBracket] = useState(false);

  // ── Symbol Search ───────────────────────────────────────────────────────
  const [symbolSearch, setSymbolSearch] = useState('');
  const [showSymbolDropdown, setShowSymbolDropdown] = useState(false);
  const symbolInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // ── Data State ──────────────────────────────────────────────────────────
  const [quote, setQuote] = useState<QuoteData | null>(null);
  const [account, setAccount] = useState<BrokerAccount | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  // ── UI State ────────────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);
  const [showOrderTypeInfo, setShowOrderTypeInfo] = useState(false);

  const toastTimer = useRef<ReturnType<typeof setTimeout>>();

  // ── Derived ─────────────────────────────────────────────────────────────
  const isBuy = side === 'buy';
  const accentColor = isBuy ? '#10b981' : '#ef4444';
  const accentBg = isBuy ? 'bg-emerald-500' : 'bg-red-500';
  const accentBgHover = isBuy ? 'hover:bg-emerald-600' : 'hover:bg-red-600';
  const accentBorder = isBuy ? 'border-emerald-500/30' : 'border-red-500/30';
  const accentText = isBuy ? 'text-emerald-400' : 'text-red-400';

  const needsLimitPrice = orderType === 'limit' || orderType === 'stop_limit';
  const needsStopPrice = orderType === 'stop' || orderType === 'stop_limit';
  const needsTrail = orderType === 'trailing_stop';

  const buyingPower = account ? parseFloat(account.buying_power) : 0;
  const referencePrice = quote?.last ?? 0;

  const estimatedCost = useMemo(() => {
    const q = parseFloat(qty) || 0;
    if (q <= 0 || referencePrice <= 0) return 0;
    if (orderType === 'limit' || orderType === 'stop_limit') {
      const lp = parseFloat(limitPrice) || referencePrice;
      return q * lp;
    }
    return q * referencePrice;
  }, [qty, referencePrice, orderType, limitPrice]);

  const filteredSymbols = useMemo(() => {
    const search = (symbolSearch || symbol).toUpperCase().trim();
    if (!search) return COMMON_SYMBOLS.slice(0, 8);
    return COMMON_SYMBOLS.filter(s => s.includes(search)).slice(0, 8);
  }, [symbolSearch, symbol]);

  // ── Fetch Account ───────────────────────────────────────────────────────
  useEffect(() => {
    brokerAPI.getAccount()
      .then(setAccount)
      .catch(() => { /* silent — account display is optional */ });
  }, []);

  // ── Fetch Quote on Symbol Change ────────────────────────────────────────
  useEffect(() => {
    if (!symbol || symbol.length < 1) {
      setQuote(null);
      return;
    }

    const timer = setTimeout(() => {
      setQuoteLoading(true);
      brokerAPI.getQuote(symbol)
        .then((data: Record<string, unknown>) => {
          setQuote({
            bid: Number(data.bid ?? data.bidprice ?? 0),
            ask: Number(data.ask ?? data.askprice ?? 0),
            last: Number(data.last ?? data.lastprice ?? data.close ?? 0),
            volume: Number(data.volume ?? 0),
            change: Number(data.change ?? 0),
            change_pct: Number(data.change_pct ?? data.change_percent ?? 0),
          });
        })
        .catch(() => setQuote(null))
        .finally(() => setQuoteLoading(false));
    }, 400);

    return () => clearTimeout(timer);
  }, [symbol]);

  // ── Close dropdown on outside click ─────────────────────────────────────
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        symbolInputRef.current &&
        !symbolInputRef.current.contains(e.target as Node)
      ) {
        setShowSymbolDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // ── Auto-dismiss toast ──────────────────────────────────────────────────
  useEffect(() => {
    if (!toast) return;
    toastTimer.current = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(toastTimer.current);
  }, [toast]);

  // ── Quick Qty ───────────────────────────────────────────────────────────
  const handleQuickQty = useCallback((fraction: number) => {
    if (buyingPower <= 0 || referencePrice <= 0) return;
    const maxShares = Math.floor((buyingPower * fraction) / referencePrice);
    setQty(maxShares > 0 ? maxShares.toString() : '1');
  }, [buyingPower, referencePrice]);

  // ── Symbol Select ───────────────────────────────────────────────────────
  const handleSymbolSelect = useCallback((s: string) => {
    setSymbol(s);
    setSymbolSearch('');
    setShowSymbolDropdown(false);
  }, []);

  // ── Submit Order ────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    // Validate
    if (!symbol.trim()) {
      setToast({ type: 'error', title: 'Missing Symbol', message: 'Enter a ticker symbol.' });
      return;
    }
    const qtyNum = parseFloat(qty);
    if (!qtyNum || qtyNum <= 0) {
      setToast({ type: 'error', title: 'Invalid Quantity', message: 'Enter a valid quantity > 0.' });
      return;
    }
    if (needsLimitPrice && (!limitPrice || parseFloat(limitPrice) <= 0)) {
      setToast({ type: 'error', title: 'Missing Limit Price', message: 'Limit price is required for this order type.' });
      return;
    }
    if (needsStopPrice && (!stopPrice || parseFloat(stopPrice) <= 0)) {
      setToast({ type: 'error', title: 'Missing Stop Price', message: 'Stop price is required for this order type.' });
      return;
    }
    if (needsTrail && (!trailValue || parseFloat(trailValue) <= 0)) {
      setToast({ type: 'error', title: 'Missing Trail Value', message: 'Enter a trail percent or price.' });
      return;
    }

    const order: PlaceOrderRequest = {
      symbol: symbol.toUpperCase().trim(),
      side,
      type: orderType,
      qty: qtyNum,
      time_in_force: tif,
    };

    if (needsLimitPrice) order.limit_price = parseFloat(limitPrice);
    if (needsStopPrice) order.stop_price = parseFloat(stopPrice);
    if (needsTrail) {
      if (trailMode === 'percent') {
        order.trail_percent = parseFloat(trailValue);
      } else {
        order.trail_price = parseFloat(trailValue);
      }
    }
    if (showBracket && takeProfitPrice && parseFloat(takeProfitPrice) > 0) {
      order.take_profit = parseFloat(takeProfitPrice);
    }
    if (showBracket && stopLossPrice && parseFloat(stopLossPrice) > 0) {
      order.stop_loss = parseFloat(stopLossPrice);
    }

    setSubmitting(true);
    try {
      const result = await brokerAPI.placeOrder(order);
      setToast({
        type: 'success',
        title: 'Order Placed',
        message: `${side.toUpperCase()} ${qtyNum} ${symbol} — ${orderType.replace('_', ' ')} order submitted.`,
      });
      onOrderPlaced?.(result.order);
      // Reset form
      setQty('');
      setLimitPrice('');
      setStopPrice('');
      setTrailValue('');
      setTakeProfitPrice('');
      setStopLossPrice('');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error placing order.';
      setToast({ type: 'error', title: 'Order Failed', message });
    } finally {
      setSubmitting(false);
    }
  }, [
    symbol, side, orderType, qty, limitPrice, stopPrice, trailMode, trailValue,
    tif, showBracket, takeProfitPrice, stopLossPrice,
    needsLimitPrice, needsStopPrice, needsTrail, onOrderPlaced,
  ]);

  // ======================================================================
  // Render
  // ======================================================================

  return (
    <div className="relative flex flex-col w-full max-w-md bg-gray-900 border border-gray-700/50 rounded-2xl shadow-2xl overflow-hidden">
      {/* ── Toast ─────────────────────────────────────────────────────── */}
      {toast && (
        <div
          className={`absolute top-3 left-3 right-3 z-50 flex items-start gap-3 p-3 rounded-lg border text-sm animate-in fade-in slide-in-from-top-2 duration-300 ${
            toast.type === 'success'
              ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
              : 'bg-red-500/10 border-red-500/30 text-red-300'
          }`}
        >
          {toast.type === 'success' ? (
            <CheckCircle2 size={18} className="mt-0.5 flex-shrink-0 text-emerald-400" />
          ) : (
            <AlertTriangle size={18} className="mt-0.5 flex-shrink-0 text-red-400" />
          )}
          <div className="flex-1 min-w-0">
            <p className="font-semibold">{toast.title}</p>
            <p className="text-xs opacity-80 mt-0.5">{toast.message}</p>
          </div>
          <button
            onClick={() => setToast(null)}
            className="text-gray-400 hover:text-white flex-shrink-0"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* ── Header ────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center"
            style={{ background: `${accentColor}18`, border: `1px solid ${accentColor}40` }}
          >
            <Zap size={18} style={{ color: accentColor }} />
          </div>
          <div>
            <h2 className="text-white font-bold text-base tracking-tight">Order Ticket</h2>
            <p className="text-gray-500 text-xs">
              {account?.paper ? 'Paper Trading' : 'Live Trading'}
              {account && ` | BP: ${formatCurrency(buyingPower)}`}
            </p>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-gray-800 transition-colors"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* ── Body ──────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-4 p-5 overflow-y-auto max-h-[calc(100vh-220px)]">
        {/* ── Symbol Input ──────────────────────────────────────────── */}
        <div className="relative">
          <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
            Symbol
          </label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              ref={symbolInputRef}
              type="text"
              value={showSymbolDropdown ? symbolSearch || symbol : symbol}
              onChange={(e) => {
                const val = e.target.value.toUpperCase().replace(/[^A-Z.]/g, '');
                setSymbolSearch(val);
                setSymbol(val);
                setShowSymbolDropdown(true);
              }}
              onFocus={() => setShowSymbolDropdown(true)}
              placeholder="AAPL, MSFT, TSLA..."
              className="w-full pl-9 pr-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm font-mono placeholder-gray-600 focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/20 transition-all"
              spellCheck={false}
              autoComplete="off"
            />
          </div>

          {/* Symbol Dropdown */}
          {showSymbolDropdown && (
            <div
              ref={dropdownRef}
              className="absolute z-40 top-full mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg shadow-xl overflow-hidden"
            >
              {filteredSymbols.length > 0 ? (
                filteredSymbols.map((s) => (
                  <button
                    key={s}
                    onClick={() => handleSymbolSelect(s)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                  >
                    <span className="font-mono font-semibold text-white">{s}</span>
                  </button>
                ))
              ) : (
                <div className="px-3 py-2 text-sm text-gray-500">No matches</div>
              )}
            </div>
          )}

          {/* Live Quote Bar */}
          {symbol && (
            <div className="mt-2 flex items-center gap-3 text-xs">
              {quoteLoading ? (
                <span className="text-gray-500 flex items-center gap-1">
                  <Loader2 size={12} className="animate-spin" /> Loading quote...
                </span>
              ) : quote ? (
                <>
                  <span className="text-white font-mono font-bold text-sm">
                    {formatCurrency(quote.last)}
                  </span>
                  <span className={quote.change >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                    {quote.change >= 0 ? '+' : ''}
                    {quote.change.toFixed(2)} ({quote.change_pct.toFixed(2)}%)
                  </span>
                  <span className="text-gray-600 ml-auto">
                    Bid {quote.bid.toFixed(2)} / Ask {quote.ask.toFixed(2)}
                  </span>
                </>
              ) : (
                <span className="text-gray-600">Enter a valid symbol</span>
              )}
            </div>
          )}
        </div>

        {/* ── Side Toggle ───────────────────────────────────────────── */}
        <div>
          <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
            Side
          </label>
          <div className="grid grid-cols-2 gap-1 p-1 bg-gray-800 rounded-lg">
            <button
              onClick={() => setSide('buy')}
              className={`flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-bold transition-all ${
                side === 'buy'
                  ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-gray-750'
              }`}
            >
              <TrendingUp size={16} />
              BUY
            </button>
            <button
              onClick={() => setSide('sell')}
              className={`flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-bold transition-all ${
                side === 'sell'
                  ? 'bg-red-500 text-white shadow-lg shadow-red-500/20'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-gray-750'
              }`}
            >
              <TrendingDown size={16} />
              SELL
            </button>
          </div>
        </div>

        {/* ── Order Type ────────────────────────────────────────────── */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
              Order Type
            </label>
            <button
              onClick={() => setShowOrderTypeInfo(!showOrderTypeInfo)}
              className="text-gray-600 hover:text-gray-400 transition-colors"
            >
              <Info size={12} />
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {ORDER_TYPES.map((ot) => (
              <button
                key={ot.value}
                onClick={() => setOrderType(ot.value)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-all ${
                  orderType === ot.value
                    ? 'bg-purple-500/15 border-purple-500/40 text-purple-300'
                    : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600 hover:text-gray-300'
                }`}
                title={ot.desc}
              >
                {ot.label}
              </button>
            ))}
          </div>
          {showOrderTypeInfo && (
            <p className="mt-1.5 text-[11px] text-gray-500 italic">
              {ORDER_TYPES.find(o => o.value === orderType)?.desc}
            </p>
          )}
        </div>

        {/* ── Quantity ──────────────────────────────────────────────── */}
        <div>
          <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
            Quantity (Shares)
          </label>
          <input
            type="number"
            min="0"
            step="1"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            placeholder="0"
            className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm font-mono placeholder-gray-600 focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/20 transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          {/* Quick qty buttons */}
          <div className="flex gap-1.5 mt-2">
            {QTY_PRESETS.map((preset) => (
              <button
                key={preset.label}
                onClick={() => handleQuickQty(preset.fraction)}
                disabled={buyingPower <= 0 || referencePrice <= 0}
                className="flex-1 py-1.5 rounded-md text-[11px] font-bold bg-gray-800 border border-gray-700 text-gray-400 hover:border-purple-500/40 hover:text-purple-300 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                {preset.label}
              </button>
            ))}
          </div>
          {buyingPower > 0 && referencePrice > 0 && (
            <p className="mt-1 text-[11px] text-gray-600">
              Max ~{formatNumber(Math.floor(buyingPower / referencePrice))} shares at current price
            </p>
          )}
        </div>

        {/* ── Limit Price ───────────────────────────────────────────── */}
        {needsLimitPrice && (
          <div>
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
              <DollarSign size={10} className="inline mr-0.5" />
              Limit Price
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={limitPrice}
              onChange={(e) => setLimitPrice(e.target.value)}
              placeholder={referencePrice ? referencePrice.toFixed(2) : '0.00'}
              className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm font-mono placeholder-gray-600 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20 transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          </div>
        )}

        {/* ── Stop Price ────────────────────────────────────────────── */}
        {needsStopPrice && (
          <div>
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
              <Shield size={10} className="inline mr-0.5" />
              Stop Price
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={stopPrice}
              onChange={(e) => setStopPrice(e.target.value)}
              placeholder="0.00"
              className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm font-mono placeholder-gray-600 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          </div>
        )}

        {/* ── Trailing Stop ─────────────────────────────────────────── */}
        {needsTrail && (
          <div>
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
              Trail Amount
            </label>
            <div className="flex gap-2">
              <div className="flex p-0.5 bg-gray-800 rounded-md border border-gray-700">
                <button
                  onClick={() => setTrailMode('percent')}
                  className={`px-2.5 py-1 rounded text-xs font-semibold transition-all ${
                    trailMode === 'percent'
                      ? 'bg-purple-500/20 text-purple-300'
                      : 'text-gray-500 hover:text-gray-300'
                  }`}
                >
                  %
                </button>
                <button
                  onClick={() => setTrailMode('price')}
                  className={`px-2.5 py-1 rounded text-xs font-semibold transition-all ${
                    trailMode === 'price'
                      ? 'bg-purple-500/20 text-purple-300'
                      : 'text-gray-500 hover:text-gray-300'
                  }`}
                >
                  $
                </button>
              </div>
              <input
                type="number"
                min="0"
                step={trailMode === 'percent' ? '0.1' : '0.01'}
                value={trailValue}
                onChange={(e) => setTrailValue(e.target.value)}
                placeholder={trailMode === 'percent' ? '1.0' : '0.50'}
                className="flex-1 px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm font-mono placeholder-gray-600 focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/20 transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>
          </div>
        )}

        {/* ── Time in Force ─────────────────────────────────────────── */}
        <div>
          <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
            <Clock size={10} className="inline mr-0.5" />
            Time in Force
          </label>
          <div className="flex gap-1.5">
            {TIME_IN_FORCE_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => setTif(option.value)}
                className={`flex-1 py-1.5 rounded-md text-xs font-bold border transition-all ${
                  tif === option.value
                    ? 'bg-blue-500/15 border-blue-500/40 text-blue-300'
                    : 'bg-gray-800 border-gray-700 text-gray-500 hover:border-gray-600 hover:text-gray-400'
                }`}
                title={option.desc}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Bracket Orders (TP / SL) ──────────────────────────────── */}
        <div>
          <button
            onClick={() => setShowBracket(!showBracket)}
            className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-300 transition-colors"
          >
            <ChevronDown
              size={14}
              className={`transition-transform ${showBracket ? 'rotate-180' : ''}`}
            />
            <span className="font-semibold uppercase tracking-wider">
              Bracket Order (TP / SL)
            </span>
          </button>

          {showBracket && (
            <div className="mt-3 grid grid-cols-2 gap-3">
              {/* Take Profit */}
              <div>
                <label className="block text-[11px] font-semibold text-emerald-500/70 uppercase tracking-wider mb-1.5">
                  <Target size={10} className="inline mr-0.5" />
                  Take Profit
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={takeProfitPrice}
                  onChange={(e) => setTakeProfitPrice(e.target.value)}
                  placeholder="0.00"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-emerald-300 text-sm font-mono placeholder-gray-600 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20 transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
              {/* Stop Loss */}
              <div>
                <label className="block text-[11px] font-semibold text-red-500/70 uppercase tracking-wider mb-1.5">
                  <Shield size={10} className="inline mr-0.5" />
                  Stop Loss
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={stopLossPrice}
                  onChange={(e) => setStopLossPrice(e.target.value)}
                  placeholder="0.00"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-red-300 text-sm font-mono placeholder-gray-600 focus:outline-none focus:border-red-500/50 focus:ring-1 focus:ring-red-500/20 transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Footer: Cost + Submit ──────────────────────────────────────── */}
      <div className="border-t border-gray-800 px-5 py-4 bg-gray-900/80 backdrop-blur-sm">
        {/* Estimated Cost */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider">
            Est. {isBuy ? 'Cost' : 'Proceeds'}
          </span>
          <span className={`text-lg font-bold font-mono ${estimatedCost > 0 ? 'text-white' : 'text-gray-600'}`}>
            {estimatedCost > 0 ? formatCurrency(estimatedCost) : '--'}
          </span>
        </div>

        {/* Buying Power Warning */}
        {isBuy && estimatedCost > buyingPower && buyingPower > 0 && (
          <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
            <AlertTriangle size={14} className="text-amber-400 flex-shrink-0" />
            <span className="text-xs text-amber-300">
              Estimated cost exceeds buying power ({formatCurrency(buyingPower)})
            </span>
          </div>
        )}

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={submitting || !symbol || !qty}
          className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed ${accentBg} ${accentBgHover} shadow-lg ${
            isBuy ? 'shadow-emerald-500/20' : 'shadow-red-500/20'
          }`}
        >
          {submitting ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Placing Order...
            </>
          ) : (
            <>
              <Zap size={16} />
              {side.toUpperCase()} {qty ? `${qty} ` : ''}{symbol || '---'}{' '}
              {orderType === 'market' ? '@ MKT' : orderType === 'limit' ? `@ ${limitPrice || '---'}` : ''}
            </>
          )}
        </button>

        {/* Order Summary */}
        <p className="mt-2 text-center text-[11px] text-gray-600">
          {orderType.replace('_', ' ').toUpperCase()}
          {' / '}{tif.toUpperCase()}
          {showBracket && takeProfitPrice ? ` / TP ${takeProfitPrice}` : ''}
          {showBracket && stopLossPrice ? ` / SL ${stopLossPrice}` : ''}
        </p>
      </div>
    </div>
  );
}
